// hooks for SpotlightApi

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { SpotlightApi } from '../api/spotlight/api';
import { CustomerPin } from '../api/spotlight/types';

export const useGetPinsByScopeAndResourceType = (
  args:
    | {
        scopeId: number | undefined;
        scopeType: string | undefined;
        resourceType: string;
      }
    | undefined,
) => {
  const { scopeId, scopeType, resourceType } = args || {};

  return useQuery(
    [
      'spotlight',
      'getPinnedResourcesByParentAndAccountAndResourceType',
      scopeId,
      scopeType,
      resourceType,
    ],
    () => {
      if (!args) {
        return Promise.resolve([]);
      }

      return scopeId && scopeType && resourceType
        ? SpotlightApi.getPinsByScopeAndResource(
            scopeId,
            scopeType,
            resourceType,
          )
        : Promise.resolve([]);
    },
    {
      enabled: !!scopeId && !!scopeType,
    },
  );
};

export const useGetPinsByResourceAndScope = ({
  scopeId,
  scopeType,
  resourceId,
  resourceType,
}: {
  resourceId: number;
  resourceType: string;
  scopeId: number;
  scopeType: string;
}) => {
  return useQuery(
    [
      'spotlight',
      'getMappingByResourceAndParentAndAccount',
      resourceId,
      resourceType,
      scopeId,
      scopeType,
    ],
    () =>
      SpotlightApi.getPinByResourceAndScope(
        resourceId,
        resourceType,
        scopeId,
        scopeType,
      ),
  );
};

// upsertPinnedOnResourceAndParentAndAccount
export const useAddPinsOnResourceAndScope = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      mapping,
      scopeId,
      scopeType,
      resourceId,
      resourceType,
    }: {
      resourceId: number;
      resourceType: string;
      scopeId: number;
      scopeType: string;
      mapping: CustomerPin;
    }) => {
      return SpotlightApi.addPinOnResourceWithScope(
        resourceId,
        resourceType,
        scopeId,
        scopeType,
        mapping,
      );
    },

    {
      onSuccess: (_data, { scopeId, resourceId, scopeType, resourceType }) => {
        queryClient.invalidateQueries([
          'spotlight',
          'getMappingByResourceAndParentAndAccount',
          resourceId,
          resourceType,
          scopeId,
          scopeType,
        ]);
      },
    },
  );
};

// deleteMapping parent and resource
export const useDeleteMappingParentAndResource = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      resourceId,
      resourceType,
      scopeId,
      scopeType,
    }: {
      resourceId: number;
      resourceType: string;
      scopeId: number;
      scopeType: string;
    }) => {
      return SpotlightApi.deletePinOnResourceWithScope(
        resourceId,
        resourceType,
        scopeId,
        scopeType,
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('spotlight');
      },
    },
  );
};
